import {Switch as RadixUISwitch, SwitchThumb as RadixUISwitchThumb} from '@radix-ui/react-switch';
import {css, StyleSheet, StyleDeclaration} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {BLUE, WHITE, SILVER, BLUE_LIGHTER, GRAY} from 'ui-library/Colors';
import useHoverState from 'ui-library/hooks/useHoverState';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export enum SwitchSize {
  SMALL = 'SMALL',
  DEFAULT = 'DEFAULT',
}

interface SwitchProps {
  on: boolean;
  onChange: (status: boolean) => void;
  disabled?: boolean;
  size?: SwitchSize;
}

/**
 * A controlled binary input switch that can be toggled with a click.
 *
 * Switching between on and off is accompanied by a transition and hovering
 * over the input nudges the switch handle but it cannot be dragged.
 *
 * The component can currently be rendered two sizes: small or default.
 */
export default function Switch({
  on,
  onChange,
  disabled,
  size = SwitchSize.DEFAULT,
}: SwitchProps): JSX.Element {
  const [isHovered, hoverProps] = useHoverState();
  // Disable hover effect immediately after user clicks.
  const [canShowHoverEffect, setCanShowHoverEffect] = useState(true);
  // Re-enable hover effect after click when mouseout event occurs.
  useEffect(() => {
    if (!isHovered && !canShowHoverEffect) {
      setCanShowHoverEffect(true);
    }
  }, [isHovered, canShowHoverEffect]);

  const styles = SIZE_TO_STYLES[size];

  const switchStyles = [styles.switch];
  if (on && !disabled) {
    switchStyles.push(styles.onSwitch);
  } else if (!on && !disabled) {
    switchStyles.push(styles.offSwitch);
  } else {
    switchStyles.push(styles.disabledSwitch);
  }
  if (canShowHoverEffect && isHovered && !disabled) {
    switchStyles.push(styles.hoveredSwitch);
  }
  const handleStyles = [styles.handle];
  if (on) {
    handleStyles.push(styles.onHandle);
  } else {
    handleStyles.push(styles.offHandle);
    if (disabled) {
      handleStyles.push(styles.disabledOffHandle);
    }
  }
  if (canShowHoverEffect && isHovered && !disabled) {
    if (on) {
      handleStyles.push(styles.hoveredOnHandle);
    } else {
      handleStyles.push(styles.hoveredOffHandle);
    }
  }

  return (
    <RadixUISwitch
      checked={on}
      disabled={disabled}
      onCheckedChange={(newOn) => {
        setCanShowHoverEffect(false);
        onChange(newOn);
      }}
      className={css(...switchStyles)}
      {...hoverProps}
    >
      <RadixUISwitchThumb className={css(...handleStyles)}>
        <div className={css(styles.iconContainer)}>
          {on && (
            <BenchlingIcon
              icon="check"
              className={css(styles.checkboxIcon, disabled && styles.disabledIcon)}
            />
          )}
        </div>
      </RadixUISwitchThumb>
    </RadixUISwitch>
  );
}

const HANDLE_PADDING = 2;
const HANDLE_HOVER_ADDITIONAL_PADDING = 2;
type StyleKey =
  | 'switch'
  | 'onSwitch'
  | 'offSwitch'
  | 'disabledSwitch'
  | 'handle'
  | 'hoveredSwitch'
  | 'onHandle'
  | 'hoveredOnHandle'
  | 'offHandle'
  | 'hoveredOffHandle'
  | 'disabledOffHandle'
  | 'iconContainer'
  | 'checkboxIcon'
  | 'disabledIcon';
type SwitchStyleSheet = Record<StyleKey, StyleDeclaration>;

/**
 * Generate an Aphrodite stylesheet for styles dynamically since they are dependent on the Switch size.
 *
 * @param width Full width of the Switch component in pixels
 * @param height Full height of the Switch component in pixels
 * @param iconSize Width and height of the icon in pixels
 * @returns Aphrodite Stylesheet object
 */
function getStylesForSizes({
  width,
  height,
  iconSize,
}: {
  width: number;
  height: number;
  iconSize: number;
}): SwitchStyleSheet {
  const trackWidth = width - 2 * HANDLE_PADDING;
  const handleDiameter = height - 2 * HANDLE_PADDING;
  return StyleSheet.create({
    switch: {
      width: `${width}px`,
      height: `${height}px`,
      borderRadius: '8px',
      borderWidth: '0px',
      padding: `0px ${HANDLE_PADDING}px`,
      transition: 'backgroundColor 150ms',
    },
    hoveredSwitch: {
      backgroundColor: BLUE_LIGHTER,
    },
    onSwitch: {
      backgroundColor: BLUE,
    },
    offSwitch: {
      backgroundColor: SILVER,
    },
    disabledSwitch: {
      backgroundColor: GRAY,
      cursor: 'not-allowed',
    },
    handle: {
      width: `${handleDiameter}px`,
      height: `${handleDiameter}px`,
      display: 'block',
      backgroundColor: WHITE,
      borderRadius: '50%',
      transition: 'transform 150ms',
    },
    onHandle: {
      transform: `translateX(${trackWidth - handleDiameter}px)`,
    },
    hoveredOnHandle: {
      transform: `translateX(${trackWidth - handleDiameter - HANDLE_HOVER_ADDITIONAL_PADDING}px)`,
    },
    offHandle: {
      transform: `translateX(0px)`,
    },
    hoveredOffHandle: {
      transform: `translateX(${HANDLE_HOVER_ADDITIONAL_PADDING}px)`,
    },
    disabledOffHandle: {
      backgroundColor: SILVER,
    },
    iconContainer: {
      height: `${handleDiameter}px`,
      alignContent: 'center',
    },
    checkboxIcon: {
      height: iconSize,
      width: iconSize,
      display: 'block',
      margin: 'auto',
      color: BLUE,
    },
    disabledIcon: {
      color: SILVER,
    },
  });
}

const SIZE_TO_STYLES: Record<keyof typeof SwitchSize, SwitchStyleSheet> = {
  [SwitchSize.DEFAULT]: getStylesForSizes({width: 30, height: 16, iconSize: 6}),
  [SwitchSize.SMALL]: getStylesForSizes({width: 24, height: 13, iconSize: 5}),
};
